import { createContext, useState, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useOrder } from "../../hooks/queries/useOrder";
import { useSendItems } from "../../hooks/mutations/useSendItems";
import { fetchProductByEan } from "../../hooks/queries/useProductByEan";
import { ItemToSendApi } from "../../types/ItemToSend";

type ItemStatus =
  | "WAITING"
  | "SENDING"
  | "SUCCESS"
  | "OUT_OF_STOCK"
  | "NOT_FOUND";

interface ImportItem {
  ean: string;
  amount: number;
  code?: string;
  name?: string;
  status?: ItemStatus;
}

interface OrderImportContextData {
  listItems: ImportItem[];
  currentIndex: number;
  startProcess: (items: ImportItem[]) => Promise<void>;
}

interface OrderImportProviderProps {
  children: ReactNode;
}

export const OrderImportContext = createContext<OrderImportContextData>(
  {} as OrderImportContextData
);

function OrderImportProvider({ children }: OrderImportProviderProps) {
  // Usa React Query em vez de Redux
  const { data: orderData } = useOrder();
  const itemsOnOrder = orderData?.items || [];
  const queryClient = useQueryClient();
  const sendItemsMutation = useSendItems();

  const [listItems, setListItems] = useState<ImportItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const sendItems = async (items: ImportItem[]) => {
    const itemsOnOrderIndexados = itemsOnOrder.reduce(
      (acc, item) => {
        acc[item.mercadoria.full_cd_mercadoria] = item.qt_pedida;
        return acc;
      },
      {} as Record<string, number>
    );

    const itemsToSend: ItemToSendApi[] = items
      .filter(item => item.status === "SENDING")
      .map(item => ({
        mercadoria: item.code!,
        qtd: parseInt(String(item.amount), 10),
        placeholder:
          parseInt(String(itemsOnOrderIndexados[item.code!] ?? 0), 10) || 0,
      }));

    if (itemsToSend.length === 0) {
      return;
    }

    // Usa o hook de mutation do React Query
    sendItemsMutation.mutate(
      { items: itemsToSend, sum: true },
      {
        onSuccess: () => {
          // Atualiza o status dos itens após sucesso
          setListItems(state =>
            state.map(item => {
              if (item.status === "SENDING") {
                return {
                  ...item,
                  status: "SUCCESS" as ItemStatus,
                };
              }
              return item;
            })
          );

          setCurrentIndex(state => state + itemsToSend.length);
        },
      }
    );
  };

  const startProcess = async (items: ImportItem[]) => {
    const itemsToProcess: ImportItem[] = items.map(item => ({
      ...item,
      name: "",
      status: "WAITING" as ItemStatus,
    }));

    setListItems(itemsToProcess);

    await Promise.all(
      items.map(async item => {
        try {
          const data = await fetchProductByEan(queryClient, item.ean);
          const {
            ds_apresentacao,
            full_cd_mercadoria,
            infos: { qt_fisica, qt_reservada },
          } = data;

          const status: ItemStatus =
            qt_fisica - qt_reservada >= item.amount
              ? "SENDING"
              : "OUT_OF_STOCK";

          itemsToProcess
            .filter(itemToProcess => itemToProcess.ean === item.ean)
            .forEach(itemToProcess => {
              itemToProcess.name = ds_apresentacao;
              itemToProcess.code = full_cd_mercadoria;
              itemToProcess.status = status;
            });

          setListItems([...itemsToProcess]);
          if (status !== "SENDING") {
            setCurrentIndex(state => state + 1);
          }
        } catch {
          itemsToProcess
            .filter(itemToProcess => itemToProcess.ean === item.ean)
            .forEach(itemToProcess => {
              itemToProcess.name = "";
              itemToProcess.status = "NOT_FOUND";
            });
          setListItems([...itemsToProcess]);
          setCurrentIndex(state => state + 1);
        }
      })
    );

    sendItems(itemsToProcess);
  };

  return (
    <OrderImportContext.Provider
      value={{
        listItems,
        currentIndex,
        startProcess,
      }}
    >
      {children}
    </OrderImportContext.Provider>
  );
}

export default OrderImportProvider;
